import { useMemo } from "react";
import { useUserExercises } from "@/data/exercises";
import { ExerciseDONE, MessageEnum, ProductCourseInfo } from "@/types";
import {
  INTERNAL_DATE_FORMAT,
  INTERNAL_DATE_FORMAT_WITH_TIME,
} from "../agenda";
import {
  useMe,
  useUserProducts,
  useLoginActivity,
  useUserEventsAndMaterials,
} from "@/data/user.ts";
import dayjs from "dayjs";

interface IReturn {
  course?: ProductCourseInfo;
  message: MessageEnum;
  endDate?: string | null;
  progress?: number;
  startDate?: string | null;
  isOneToOne?: boolean;
  lessonsDone?: number;
  kickoffDate?: string;
  exercisesDone?: number;
  recentlyDoneLessons?: number;
  recentlyDoneExercises?: number;
  percentageLessonsDone?: number;
  percentageExecisesDone?: number;
}

export default function useCourseDetails(): IReturn {
  const { me } = useMe();

  const { products } = useUserProducts();
  const { exercises } = useUserExercises();
  const { eventMaterials } = useUserEventsAndMaterials();

  const {
    activity: { diffInWeeks },
  } = useLoginActivity(me?.item?.user?.token ?? "");

  const lessonsDone = useMemo(() => {
    if (!products || !products.items || !eventMaterials?.item?.events) return;

    const done = eventMaterials?.item?.events.filter(
      (e) => e?.pivot.access_date != null && e?.tutor_token != null,
    );

    const recentlyDoneLessons = done?.filter(
      (e) =>
        dayjs().diff(dayjs(e.data, INTERNAL_DATE_FORMAT_WITH_TIME), "days") < 3,
    ).length;

    return {
      lessonsDone: eventMaterials.item.lessons_attended,
      recentlyDoneLessons,
      percentageLessonsDone: eventMaterials.item.percentage_attended,
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventMaterials?.item?.events, products]);

  const payload = useMemo(() => {
    if (products && products?.items) {
      const items = products.items;

      const timeDist = items.map((item) =>
        dayjs().diff(
          dayjs(item?.pivot.start_date, INTERNAL_DATE_FORMAT_WITH_TIME),
          "day",
        ),
      );

      const idx = timeDist.indexOf(Math.min(...timeDist));

      const mostRecentProduct = items[idx];
      const isOneToOne =
        (mostRecentProduct?.course_info?.course_name
          ?.toLowerCase()
          ?.includes("lezioni individuali") ||
          !mostRecentProduct?.course_info?.course_structure?.filter(
            (c) => Number(c.ielts) || Number(c.grammar) || Number(c.speaking),
          )?.length) ??
        false;

      const endDate = mostRecentProduct?.pivot?.end_date;
      const startDate = mostRecentProduct?.pivot?.start_date;
      const progress = dayjs().isSameOrAfter(startDate)
        ? Math.min(
            100,
            (dayjs().diff(dayjs(startDate, INTERNAL_DATE_FORMAT), "days") /
              dayjs(endDate, INTERNAL_DATE_FORMAT).diff(
                dayjs(startDate, INTERNAL_DATE_FORMAT),
                "days",
              )) *
              100,
          )
        : 0;

      const recentlyDoneExercises = exercises?.done.filter(
        (e: ExerciseDONE) =>
          dayjs().diff(dayjs(e.data, INTERNAL_DATE_FORMAT_WITH_TIME), "days") <
          3,
      ).length;

      const exercisesDone =
        exercises?.exercises_done_into_current_range_product || 0;

      const exerciseToDO =
        exercises?.toDo?.filter(
          (e) => !exercises?.done?.some((d) => d.id == e.id),
        )?.length || 0;
      const exerciseDone =
        [...new Set(exercises?.done?.map((e) => e.id))]?.length || 0;

      const totalExercises = exerciseToDO + exerciseDone;
      const percentageExecisesDone = Math.ceil(
        (exerciseDone / totalExercises) * 100,
      );

      return {
        endDate,
        progress,
        startDate,
        isOneToOne,
        exercisesDone,
        recentlyDoneExercises,
        percentageExecisesDone,
        course: mostRecentProduct?.course_info,
      };
    }

    return {};
  }, [products, exercises]);

  const kickoffDateFormatted = me?.item.kickoff_date
    .split("/")
    .reverse()
    .join("-");

  const message = useMemo<MessageEnum>(() => {
    let ms = MessageEnum.DEFAULT;

    if (diffInWeeks === 1) {
      ms = MessageEnum.MISSED_LAST_WEEK;
    } else if (diffInWeeks > 1) {
      ms = MessageEnum.RETURN_AFTER_MISSING_WEEK;
    }

    if (dayjs().isBefore(dayjs(kickoffDateFormatted))) {
      if (payload?.isOneToOne) {
        ms = MessageEnum.COURSE_START_1_TO_1; // Nuovo messaggio per lezioni individuali
      } else {
        ms = MessageEnum.COURSE_START;
      }
    }

    if (
      diffInWeeks === 0 &&
      Math.ceil(dayjs().diff(dayjs(kickoffDateFormatted), "week")) >= 1 &&
      Math.ceil(dayjs().diff(dayjs(kickoffDateFormatted), "week")) <= 2
    ) {
      ms = MessageEnum.FIRST_WEEK_COMPLETE;
    }

    if (Math.floor(dayjs().diff(dayjs(payload?.endDate), "month")) == 1) {
      ms = MessageEnum.MONTH_TO_COMPLETION;
    }

    if (dayjs().isAfter(dayjs(payload?.endDate))) {
      ms = MessageEnum.COURSE_COMPLETED;
    }

    return ms;
  }, [
    diffInWeeks,
    kickoffDateFormatted,
    payload?.endDate,
    payload?.isOneToOne,
  ]);

  return {
    message,
    kickoffDate: kickoffDateFormatted,
    ...payload,
    ...lessonsDone,
  };
}
